// Entity create dialog
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useCustomerEntities } from '@/hooks/useCustomers'
import { D365_VERSIONS } from '@/lib/constants'
import { ENVIRONMENT_TYPES } from '@/types/customer'

interface EntityDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  customerId: string | null
}

export function EntityDialog({ open, onOpenChange, customerId }: EntityDialogProps) {
  const { createEntity } = useCustomerEntities(customerId)
  const [name, setName] = useState('')
  const [entityCode, setEntityCode] = useState('')
  const [version, setVersion] = useState('')
  const [envType, setEnvType] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!customerId) return
    setSaving(true)
    try {
      await createEntity({
        name,
        entity_code: entityCode || null,
        d365_version: version || null,
        environment_type: envType as any || null,
      })
      setName('')
      setEntityCode('')
      setVersion('')
      setEnvType('')
      onOpenChange(false)
    } catch (err) {
      alert('Failed to create entity: ' + (err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => onOpenChange(false)}>
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-4 text-lg font-semibold">Add D365 Entity</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Entity Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label>Entity Code</Label>
            <Input value={entityCode} onChange={(e) => setEntityCode(e.target.value)} placeholder="e.g., PROD, UAT" />
          </div>
          <div>
            <Label>D365 Version</Label>
            <Select value={version} onValueChange={setVersion}>
              <SelectTrigger><SelectValue placeholder="Select version" /></SelectTrigger>
              <SelectContent>
                {D365_VERSIONS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Environment Type</Label>
            <Select value={envType} onValueChange={setEnvType}>
              <SelectTrigger><SelectValue placeholder="Select environment" /></SelectTrigger>
              <SelectContent>
                {ENVIRONMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
